import sortBy from 'lodash/sortBy';
import { detectSpecial } from '@next-order/next-order-engine';
import { getCurrentDayFlag, getDayFlag } from '../timeHelper';

/**
 * BOGO voucher (type "4") — self-contained module.
 *
 * Mirrors the POS implementation (src/orderEngine/getBogoVoucherDiscount.js
 * in the POS repo). All BOGO logic lives here; the only integration points
 * are a single `type === '4'` branch in engineGetOrderCalculations and the
 * time-slot check in validateVoucher. Types 1/2/3 are untouched.
 *
 * Deliberate v1 decisions (must stay in parity with POS):
 * 1. Half/half and four-quarter rows never participate in BOGO.
 * 2. `maxItemPrice` is a qualification cap, not a discount cap.
 * 3. Buy/get frames are all-or-nothing.
 * 4. The get item must be a separate cart row from the buy item.
 * 5. A computed discount of 0 means the voucher is treated as not applied.
 */

/* ----------------------------- config helpers ---------------------------- */

// Every config array stores ALL options with `isActive` booleans — an
// inactive entry is an unselected option, not a filter.
const getActiveIds = (list) =>
  (list || [])
    .filter((entry) => entry && entry.isActive === true)
    .map((entry) => entry._id);

// Spend-trigger value of one cart unit: size price plus its extras. Extras
// quantities on a row are per unit. Only the spend check uses this — the
// reward discount stays on the size price.
const getRowSpendValue = (row) => {
  const extras = (row?.selectedExtraIngredients || []).reduce(
    (acc, ing) =>
      Number(ing?.quantity) > 0
        ? acc + Number(ing.quantity) * (Number(ing.price) || 0)
        : acc,
    0
  );
  return (Number(row?.price) || 0) + extras;
};

// buyConfig.triggerType: 'quantity' (default, also for older vouchers) or
// 'spend' — spend at least minSpend on matching items to unlock the reward.
export const isBogoSpendVoucher = (voucher) =>
  voucher?.type === '4' && voucher?.buyConfig?.triggerType === 'spend';

const normalizeConfig = (config, isGetConfig) => {
  const quantity = Number(config?.quantity);
  const isSpend = !isGetConfig && config?.triggerType === 'spend';
  const minSpend = Number(config?.minSpend);
  const maxItemPriceRaw = config?.maxItemPrice;
  const maxItemPrice =
    isGetConfig &&
    maxItemPriceRaw !== null &&
    maxItemPriceRaw !== undefined &&
    maxItemPriceRaw !== '' &&
    Number(maxItemPriceRaw) > 0
      ? Number(maxItemPriceRaw)
      : null;

  return {
    quantity: quantity > 0 ? Math.floor(quantity) : 1,
    // Buy-side anyItem is only offered for spend deals ("spend $X on anything").
    anyItem: config?.anyItem === true && (isGetConfig || isSpend),
    isSpend,
    minSpend: isSpend && minSpend > 0 ? minSpend : 0,
    maxItemPrice,
    categoryIds: getActiveIds(config?.categories),
    menuItemIds: getActiveIds(config?.menuItems),
    sizeIds: getActiveIds(config?.menuSizes),
    subModifierIds: getActiveIds(config?.subModifiers),
  };
};

/* ------------------------------ row matching ------------------------------ */

const rowMatchesConfig = (row, config) => {
  // 1. Whole items only — half/half and quarter rows carry mis-priced
  //    single-half values, so matching them would compute wrong discounts.
  if (row.isHalf || row.isQuarter || row.isSecondHalf) return false;

  // 2. maxItemPrice (getConfig only) is a qualification cap: pricier rows
  //    are disqualified entirely, never partially discounted.
  if (config.maxItemPrice !== null && Number(row.price) > config.maxItemPrice)
    return false;

  // 3. anyItem (getConfig only): match — skip category/size/item checks.
  if (config.anyItem) return true;

  // 4. Category must be among the active categories.
  if (
    config.categoryIds.length > 0 &&
    !config.categoryIds.includes(row.categoryId)
  )
    return false;

  // 5. Size filter: menuSizes and subModifiers are parallel filters (same
  //    convention as type 3). If both lists are empty, no size filtering.
  const hasSizeFilter =
    config.sizeIds.length > 0 || config.subModifierIds.length > 0;
  if (
    hasSizeFilter &&
    !config.sizeIds.includes(row.sizeKey) &&
    !config.subModifierIds.includes(row.sizeKey)
  )
    return false;

  // 6. If active menuItems is non-empty, the row's item must be in it.
  if (config.menuItemIds.length > 0 && !config.menuItemIds.includes(row._id))
    return false;

  return true;
};

/* ------------------------------ benefit math ------------------------------ */

// Discount per matched get row, computed on the size price only (extra
// ingredients / modifier surcharges excluded — same convention as type 3).
const getBenefitForRow = (row, benefit) => {
  const unitPrice = Number(row.price) || 0;
  const benefitType = benefit?.benefitType || '3';

  if (benefitType === '1') {
    // Percentage: min(unitPrice × value / 100, maxDiscountAmount if > 0, unitPrice)
    const percentage = Number(benefit?.discountValue) || 0;
    let discount = (unitPrice * percentage) / 100;
    const cap = Number(benefit?.maxDiscountAmount);
    if (cap > 0) discount = Math.min(discount, cap);
    return Math.min(Math.max(discount, 0), unitPrice);
  }

  if (benefitType === '2') {
    // Fixed: min(value, unitPrice) — never negative.
    const value = Number(benefit?.discountValue) || 0;
    return Math.min(Math.max(value, 0), unitPrice);
  }

  // '3' free — full unit (size) price.
  return unitPrice;
};

/* -------------------------- candidate ordering ---------------------------- */

// Pricing strategy = candidate ordering. No post-hoc reassignment needed.
//  "1": buy fills price-descending, get fills price-ascending
//       (customer pays the priciest, cheapest is rewarded)
//  "2": buy ascending, get descending (priciest item is rewarded)
//  "3": natural order — the order the items were added to the cart.
//       The rows arrive price-sorted from the engine, so cart order must be
//       restored via primaryKey (assigned in cart explosion order).
//       Without this, a "buy 2 get 1" over overlapping pools can consume
//       the intended get item in the buy pass and discount the wrong row.
// Units the customer picked for a role in the picker (bogoPinRole) go first,
// unpinned units next, units pinned to the other role last — so the deal
// keeps the customer's choices instead of re-picking by price.
const getPinRank = (row, role) => {
  if (row.bogoPinRole === role) return 0;
  return row.bogoPinRole ? 2 : 1;
};

const orderByStrategy = (rows, role, pricingStrategy) => {
  if (pricingStrategy === '3') return sortBy(rows, 'primaryKey');
  const ascending = sortBy(rows, (r) => Number(r.price));
  if (pricingStrategy === '2')
    return role === 'buy' ? ascending : [...ascending].reverse();
  // default "1"
  return role === 'buy' ? [...ascending].reverse() : ascending;
};

const orderCandidates = (rows, role, pricingStrategy) =>
  sortBy(orderByStrategy(rows, role, pricingStrategy), (row) =>
    getPinRank(row, role)
  );

// Cart lines added through the picker carry `bogoPin: { voucherId, buy, get }`
// unit counts; tag that many of each line's unit rows with their role.
const applyBogoPins = (voucher, currentOrder, rowItems) => {
  const pinsByOrderIndex = {};
  (currentOrder?.menuItems || []).forEach((item) => {
    if (item?.bogoPin?.voucherId === voucher._id) {
      pinsByOrderIndex[item.orderIndex] = {
        buy: Number(item.bogoPin.buy) || 0,
        get: Number(item.bogoPin.get) || 0,
      };
    }
  });
  if (!Object.keys(pinsByOrderIndex).length) return rowItems;

  const used = {};
  return sortBy(rowItems || [], 'primaryKey').map((row) => {
    const pin = pinsByOrderIndex[row.orderIndex];
    if (!pin || row.isHalf || row.isSecondHalf) return row;
    const count = used[row.orderIndex] || 0;
    used[row.orderIndex] = count + 1;
    if (count < pin.buy) return { ...row, bogoPinRole: 'buy' };
    if (count < pin.buy + pin.get) return { ...row, bogoPinRole: 'get' };
    return row;
  });
};

/* --------------------------- frame construction --------------------------- */

// One application of the deal: fill the buy frame, then the get frame from
// the remaining rows (or get-first when buyFirst is false). Each frame is
// all-or-nothing; returns null when either frame can't be filled completely.
const fillFrames = (availableRows, buyConfig, getConfig, pricingStrategy, buyFirst) => {
  const takeRows = (config, role, excludedKeys) => {
    const candidates = orderCandidates(
      availableRows.filter(
        (row) =>
          !excludedKeys.has(row.primaryKey) && rowMatchesConfig(row, config)
      ),
      role,
      pricingStrategy
    );
    if (candidates.length < config.quantity) return null;
    return candidates.slice(0, config.quantity);
  };

  const nothing = new Set();
  if (buyFirst) {
    const buyRows = takeRows(buyConfig, 'buy', nothing);
    if (!buyRows) return null;
    const getRows = takeRows(
      getConfig,
      'get',
      new Set(buyRows.map((row) => row.primaryKey))
    );
    if (!getRows) return null;
    return { buyRows, getRows };
  }

  const getRows = takeRows(getConfig, 'get', nothing);
  if (!getRows) return null;
  const buyRows = takeRows(
    buyConfig,
    'buy',
    new Set(getRows.map((row) => row.primaryKey))
  );
  if (!buyRows) return null;
  return { buyRows, getRows };
};

// Spend trigger: reward rows are picked first and never count toward the
// spend. Only the rows needed to reach minSpend are consumed (highest spend
// value first) so the rest stay available for specials. With overlapping
// pools the first reward choice can leave too little spend, so the next
// reward candidates are tried in pricing-strategy order.
const fillSpendFrames = (availableRows, buyConfig, getConfig, pricingStrategy) => {
  const getPool = orderCandidates(
    availableRows.filter((row) => rowMatchesConfig(row, getConfig)),
    'get',
    pricingStrategy
  );

  for (let start = 0; start + getConfig.quantity <= getPool.length; start++) {
    const getRows = getPool.slice(start, start + getConfig.quantity);
    const getKeys = new Set(getRows.map((row) => row.primaryKey));
    const buyPool = sortBy(
      availableRows.filter(
        (row) => !getKeys.has(row.primaryKey) && rowMatchesConfig(row, buyConfig)
      ),
      [(row) => getPinRank(row, 'buy'), (row) => -getRowSpendValue(row)]
    );

    const buyRows = [];
    let spent = 0;
    for (const row of buyPool) {
      if (spent >= buyConfig.minSpend) break;
      buyRows.push(row);
      spent += getRowSpendValue(row);
    }
    if (spent >= buyConfig.minSpend) return { buyRows, getRows };
  }
  return null;
};

/**
 * How far the cart is from a spend-trigger voucher's minimum spend. Counts
 * every matching row (a reward item already in the cart included), so it is
 * a hint for UI messages, not the qualification check.
 * Returns null for quantity-trigger vouchers.
 */
export const getBogoSpendProgress = (voucher, rowItems) => {
  const buyConfig = normalizeConfig(voucher?.buyConfig, false);
  if (!buyConfig.isSpend) return null;
  const spent = (rowItems || [])
    .filter((row) => rowMatchesConfig(row, buyConfig))
    .reduce((acc, row) => acc + getRowSpendValue(row), 0);
  return {
    spent: Number(spent.toFixed(2)),
    minSpend: buyConfig.minSpend,
    remaining: Number(Math.max(0, buyConfig.minSpend - spent).toFixed(2)),
  };
};

/* ------------------------------- detection -------------------------------- */

export const detectBogoDiscount = (voucher, rowItems) => {
  const result = { voucherDiscount: 0, usedIndexes: [], usedRows: [] };

  const buyConfig = normalizeConfig(voucher?.buyConfig, false);
  const getConfig = normalizeConfig(voucher?.getConfig, true);
  const benefit = voucher?.benefit || {};
  const pricingStrategy = voucher?.pricingStrategy || '1';
  const maxApplicationsRaw = Number(voucher?.maxApplicationsPerOrder);
  const maxApplications =
    maxApplicationsRaw > 0 ? Math.floor(maxApplicationsRaw) : 1;

  let availableRows = (rowItems || []).filter((row) => !row.isSecondHalf);
  let totalDiscount = 0;

  for (let application = 0; application < maxApplications; application++) {
    // Buy-first is the primary pass. Fallback (important): if the greedy buy
    // pass consumed the only row the get pool could use (e.g. buy = any
    // pizza, get = Large only, cart has one Large), retry get-first —
    // without this, valid carts get rejected.
    const frames = buyConfig.isSpend
      ? fillSpendFrames(availableRows, buyConfig, getConfig, pricingStrategy)
      : fillFrames(availableRows, buyConfig, getConfig, pricingStrategy, true) ||
        fillFrames(availableRows, buyConfig, getConfig, pricingStrategy, false);
    if (!frames) break;

    totalDiscount += frames.getRows.reduce(
      (acc, row) => acc + getBenefitForRow(row, benefit),
      0
    );

    // Both buy and get rows are consumed — the paid item must never
    // double-dip into another application, a special, or another discount.
    const consumedKeys = new Set(
      [...frames.buyRows, ...frames.getRows].map((row) => row.primaryKey)
    );
    result.usedIndexes.push(...consumedKeys);
    result.usedRows.push(
      ...frames.buyRows.map((row) => ({ ...row, bogoRole: 'buy' })),
      ...frames.getRows.map((row) => ({
        ...row,
        bogoRole: 'get',
        bogoDiscount: getBenefitForRow(row, benefit),
      }))
    );
    availableRows = availableRows.filter(
      (row) => !consumedKeys.has(row.primaryKey)
    );
  }

  result.voucherDiscount = Number(totalDiscount.toFixed(2));
  return result;
};

/* -------------------------- availability gates ---------------------------- */

// Pickup orders require isAvailablePickup, delivery orders require
// isAvailableDelivery; walk-in ('0') and dine-in ('3') always pass. The
// day-of-week flag is checked against the delivery date if set, otherwise
// the store's current day (same as getPersonalizedSpecialFrames).
const isBogoVoucherAvailableForOrder = (voucher, currentOrder, storeConfig) => {
  if (currentOrder.orderType === '1' && !voucher.isAvailablePickup)
    return false;
  if (currentOrder.orderType === '2' && !voucher.isAvailableDelivery)
    return false;

  const dayText = currentOrder.deliveryDate
    ? getDayFlag(currentOrder.deliveryDate, 'YYYY-MM-DD HH:mm').dayText
    : getCurrentDayFlag(storeConfig?.timeZone)?.dayText;

  return Boolean(
    dayText &&
      Object.prototype.hasOwnProperty.call(voucher, dayText) &&
      voucher[dayText] === true
  );
};

/* ------------------------------ main export ------------------------------- */

// Same result shape as the engine's getVoucherDiscountedAmount so the
// caller-side handling (including the isItemOccupiedWithSpecials specials
// recompute) works identically to type 3.
export function getBogoVoucherDiscountedAmount(
  selectedVoucher,
  currentOrder,
  rowItems,
  specialDiscount,
  allSpecials,
  orderSetup,
  publicHolidays,
  storeConfig = {}
) {
  const resObj = {
    discount: 0,
    isVoucherValid: false,
    isItemOccupiedWithSpecials: false,
    usedRows: [],
  };

  if (!currentOrder || selectedVoucher?.type !== '4') return resObj;
  if (!isBogoVoucherAvailableForOrder(selectedVoucher, currentOrder, storeConfig))
    return resObj;

  rowItems = applyBogoPins(selectedVoucher, currentOrder, rowItems);

  // Pass over all rows (including rows already consumed by specials).
  const discountWithoutFlag = detectBogoDiscount(selectedVoucher, rowItems);

  if (!selectedVoucher.isAvailableWithSpecials) {
    // Pass excluding rows already consumed by specials.
    const discountFlag = detectBogoDiscount(
      selectedVoucher,
      (rowItems || []).filter((row) => !row.itemsUsedInSpecials)
    );

    if (
      discountWithoutFlag.voucherDiscount !== 0 &&
      discountFlag.voucherDiscount === 0
    ) {
      // The voucher only works using rows occupied by specials. Recompute
      // what the specials would be worth WITHOUT those rows, then keep
      // whichever deal (voucher vs. specials) gives the customer more.
      const remainingRows = sortBy(
        (rowItems || []).filter(
          (row) => !discountWithoutFlag.usedIndexes.includes(row.primaryKey)
        ),
        'price'
      ).reverse();
      const specialObj = detectSpecial(
        currentOrder,
        remainingRows,
        allSpecials,
        orderSetup,
        publicHolidays,
        storeConfig
      );
      const specials = specialObj.specials || [];
      let specialsDiscountWithoutVoucherRows = 0;
      specials.forEach((s) => {
        specialsDiscountWithoutVoucherRows += Number(s.discount);
      });

      if (
        Number(specialDiscount) - specialsDiscountWithoutVoucherRows >
        discountWithoutFlag.voucherDiscount
      ) {
        // Keeping the specials is worth more — voucher does not apply.
        resObj.isItemOccupiedWithSpecials = false;
        resObj.isVoucherValid = false;
        resObj.discount = discountFlag.voucherDiscount;
      } else {
        resObj.isItemOccupiedWithSpecials = true;
        resObj.isVoucherValid = true;
        resObj.specials = specials;
        resObj.discount = discountWithoutFlag.voucherDiscount;
        resObj.usedRows = discountWithoutFlag.usedRows;
      }
    } else {
      resObj.discount = discountFlag.voucherDiscount;
      resObj.isVoucherValid = true;
      resObj.usedRows = discountFlag.usedRows;
    }
  } else {
    resObj.isVoucherValid = true;
    resObj.discount = discountWithoutFlag.voucherDiscount;
    resObj.usedRows = discountWithoutFlag.usedRows;
  }

  // A zero discount means the deal could not be applied to this cart —
  // treat the voucher as not applied (existing generic behavior).
  if (!(Number(resObj.discount) > 0)) {
    resObj.isVoucherValid = false;
    resObj.discount = 0;
    resObj.usedRows = [];
  }

  return resObj;
}
